// Use backend CMS API for secure job application submissions
const CMS_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337/api';

export const jobApplicationService = {
  async checkDuplicate({ email, mobile, jobCircularId, clientSite }) {
    const params = new URLSearchParams({ job_circular_id: jobCircularId });
    if (email) params.append('email', email);
    if (mobile) params.append('mobile', mobile);
    if (clientSite) params.append('client_site', clientSite);
    
    const res = await fetch(`${CMS_BASE_URL}/external-data/job-application/duplicate?${params.toString()}`);
    if (!res.ok) {
      throw new Error('Failed to check duplicate');
    }
    return res.json();
  },
  async submitJobApplication(formData, jobCircularId, jobInfo) {
    try {
      // Create FormData object for file upload
      const submitData = new FormData();


      // Add all form fields to FormData
      submitData.append('job_circular_id', jobCircularId);
      submitData.append('name', formData.name || '');
      submitData.append('email', formData.email || '');
      submitData.append('mobile', formData.mobileNo || '');
      submitData.append('nid', formData.nid || '');
      submitData.append('dob', formData.dateOfBirth || '');
      submitData.append('permanent_address', formData.permanentAddress || '');
      submitData.append('present_address', formData.presentAddress || '');
      submitData.append('blood_group', formData.bloodGroup || '');
      submitData.append('gender', formData.gender || '');
      submitData.append('education', formData.latestEducationalQualification || '');
      submitData.append('source', formData.recruitmentSource || '');
      submitData.append('objective', formData.careerObjective || '');
      submitData.append('achievement', formData.achievement || '');
      submitData.append('message', formData.messageToRecruiter || '');

      // Add internship-specific fields if job is internship
      if (jobInfo && jobInfo.circular_type === 'internship') {
        submitData.append('institute_name', formData.instituteName || '');
        submitData.append('last_cgpa', formData.lastCGPA || '');
        // Handle area of interest as array
        const areaOfInterest = Array.isArray(formData.areaOfInterest) 
          ? formData.areaOfInterest.join(', ') 
          : (formData.areaOfInterest || '');
        submitData.append('area_of_interest', areaOfInterest);
        
        // Add all Part 2 questions for internships
        submitData.append('knowledge_about_company', formData.knowledgeAboutCompany || '');
        submitData.append('passion', formData.passion || '');
        submitData.append('top_skills', formData.topSkills || '');
        submitData.append('biggest_challenge', formData.biggestChallenge || '');
        submitData.append('task_completion_rate', formData.taskCompletionRate || '');
        submitData.append('hard_working', formData.hardWorking || '');
        submitData.append('punctuality', formData.punctuality || '');
        submitData.append('problem_solving', formData.problemSolving || '');
        submitData.append('intellectual_curiosity', formData.intellectualCuriosity || '');
        submitData.append('criticize_freely', formData.criticizeFreely || '');
        submitData.append('communication_skills', formData.communicationSkills || '');
        submitData.append('chatgpt_proficiency', formData.chatgptProficiency || '');
        submitData.append('business_acumen', formData.businessAcumen || '');
        submitData.append('job_suitability', formData.jobSuitability || '');
        submitData.append('work_duration', formData.workDuration || '');
        submitData.append('interest_reason', formData.interestReason || '');
      }

      
      // Add job information if provided
      if (jobInfo) {
        submitData.append('job_title', jobInfo.job_title || jobInfo.title || '');
        submitData.append('job_location', jobInfo.job_location || jobInfo.location || '');
        submitData.append('job_category', jobInfo.category_name || jobInfo.category || '');
        submitData.append('job_employment_type', jobInfo.circular_type || jobInfo.employmentType || jobInfo.duration || '');
        submitData.append('job_circular_type', jobInfo.circular_type || ''); // Add the direct circular_type field
        submitData.append('job_salary', jobInfo.salary || '');
        submitData.append('job_experience', jobInfo.job_experience || jobInfo.experience || '');
        submitData.append('job_deadline', jobInfo.deadline || '');
        submitData.append('job_description', jobInfo.job_context || jobInfo.description || '');
        submitData.append('job_requirements', jobInfo.job_requirement || jobInfo.requirements || '');
        submitData.append('job_team_name', jobInfo.team_name || '');
        submitData.append('job_team_id', jobInfo.team_id);
        submitData.append('job_member_id', jobInfo.member_id);
        submitData.append('job_circular_type', jobInfo.circular_type);
        submitData.append('job_category_id', jobInfo.category_id || '');
        submitData.append('job_vacancy', jobInfo.job_vacancy || '');
        submitData.append('job_slug', jobInfo.slug || '');
        submitData.append('job_responsibility', jobInfo.responsibility || '');
        submitData.append('job_education_requirement', jobInfo.education_requirement || '');
        submitData.append('job_benefits', jobInfo.benefits || '');
        submitData.append('job_sitting_place', jobInfo.sitting_place || '');
        submitData.append('job_device_availability', jobInfo.device_availability || '');
      }
      
      // Add CV file
      if (formData.cv) {
        submitData.append('pdf_file', formData.cv);
      }
      
      // Add reCAPTCHA token if present
      if (formData.recaptchaToken) {
        submitData.append('recaptcha_token', formData.recaptchaToken);
      }

      // Add Part 2 questions with question_ prefix
      const part2Questions = {
          'question_0': formData.knowledgeAboutCompany || '',
          'question_1': formData.passion || '',
          'question_2': formData.topSkills || '',
          'question_3': formData.biggestChallenge || '',
          'question_4': '',
          'question_5': formData.taskCompletionRate || '',
          'question_6': formData.hardWorking || '',
          'question_7': formData.punctuality || '',
          'question_8': formData.problemSolving || '',
          'question_9': formData.intellectualCuriosity || '',
          'question_10': formData.criticizeFreely || '',
          'question_11': formData.communicationSkills || '',
          'question_12': formData.chatgptProficiency || '',
          'question_13': formData.businessAcumen || '',
          'question_14': formData.jobSuitability || '',
          'question_15': formData.workDuration || '',
          'question_16': formData.interestReason || ''
        };

      // Add internship-specific questions conditionally
      if (jobInfo && jobInfo.circular_type === 'internship') {
        part2Questions['question_17'] = formData.instituteName || '';
        part2Questions['question_18'] = formData.lastCGPA || '';
        part2Questions['question_19'] = Array.isArray(formData.areaOfInterest) 
          ? formData.areaOfInterest 
          : (formData.areaOfInterest ? [formData.areaOfInterest] : []);
      }

      
      // Add questions to FormData
      Object.keys(part2Questions).forEach(key => {
        if (key === 'question_19' && Array.isArray(part2Questions[key])) {
          // Handle question_19 as array format
          part2Questions[key].forEach((item, index) => {
            submitData.append(`${key}[${index}]`, item);
          });
        } else {
          submitData.append(key, part2Questions[key]);
        }
      });

      console.log('Submitting job application with data:', Object.fromEntries(submitData.entries()));

      // Submit job application through CMS (credentials are secure on server-side)
      // Send FormData directly to CMS which will forward it to the job API
      const response = await fetch(`${CMS_BASE_URL}/external-data/job-application`, {
        method: 'POST',
        body: submitData, // Send FormData directly (don't JSON.stringify it)
        headers: {
          'Accept': 'application/json',
          // Don't set Content-Type - let the browser set it for FormData with boundary
        }
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error?.message) {
            errorMessage += `, message: ${errorData.error.message}`;
          } else if (errorData.message) {
            errorMessage += `, message: ${errorData.message}`;
          }
        } catch (e) {
          // If response is not JSON, get text
          const errorText = await response.text();
          errorMessage += `, response: ${errorText.substring(0, 200)}...`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      // Handle the response structure from CMS
      if (result.success) {
        return {
          success: true,
          message: result.message || 'Application submitted successfully',
          data: result.data
        };
      } else {
        throw new Error(result.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Job application submission error:', error);
      throw error;
    }
  }
};
